import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import featureFlags from "@/config/featureFlags.json";

const linkItems = [
  { key: "inicio", href: "/#inicio" },
  { key: "nosotros", href: "/#nosotros" },
  { key: "servicios", href: "/#servicios" },
  { key: "metodologia", href: "/#metodologia" },
  { key: "normas", href: "/#normas" },
  ...(featureFlags.showSuccessCases ? [{ key: "casos", href: "/casos-de-exito" }] : []),
  { key: "clientes", href: "/#clientes" },
  { key: "contacto", href: "/#contacto" },
];

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ${
          scrolled ? "bg-[#F9F6F0]/85 backdrop-blur-xl border-b border-[#1A1A1A]/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3" aria-label="KB Consultoría">
            <img src="/kb-logo.png" alt="KB Consultoría" className="h-12 w-auto md:hidden" />
            <img
              src="/kb-consultoria-logo-horizontal.png"
              alt="KB Consultoría"
              className="hidden md:block h-auto w-[165px] lg:w-[150px] xl:w-[190px] 2xl:w-[210px]"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-[13px] font-medium tracking-tight text-[#1A1A1A]">
            {linkItems.map((it) => (
              <Link
                key={it.key}
                to={it.href}
                data-testid={`nav-${it.key}`}
                className="link-underline hover:text-[#1B3B2B] transition-colors"
              >
                {t(`nav.${it.key}`)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1 text-[11px] tracking-widest uppercase text-[#4A4A4A]">
              <button
                type="button"
                data-testid="lang-es"
                onClick={() => setLang("es")}
                className={`px-1.5 py-0.5 ${lang === "es" ? "text-[#1A1A1A]" : "hover:text-[#1A1A1A]"}`}
                aria-pressed={lang === "es"}
              >
                ES
              </button>
              <span>·</span>
              <button
                type="button"
                data-testid="lang-en"
                onClick={() => setLang("en")}
                className={`px-1.5 py-0.5 ${lang === "en" ? "text-[#1A1A1A]" : "hover:text-[#1A1A1A]"}`}
                aria-pressed={lang === "en"}
              >
                EN
              </button>
            </div>

            <a
              href="/#contacto"
              data-testid="header-cta"
              className="hidden md:inline-flex kb-btn text-[11px] py-2.5 px-4"
            >
              {t("nav.cta")}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>

            <button
              type="button"
              aria-label="Abrir menú"
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#1A1A1A]/20"
              onClick={() => setOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#F9F6F0]"
            data-testid="mobile-menu"
          >
            <div className="h-full flex flex-col p-6">
              <div className="flex items-center justify-between mb-10">
                <img src="/kb-logo.png" alt="KB Consultoría" className="h-10 w-auto" />
                <button
                  aria-label="Cerrar menú"
                  data-testid="mobile-menu-close"
                  onClick={() => setOpen(false)}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#1A1A1A]/20"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 flex flex-col gap-1">
                {linkItems.map((it, i) => (
                  <motion.div
                    key={it.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 * i, ease: [0.76, 0, 0.24, 1] }}
                  >
                    <Link
                      to={it.href}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${it.key}`}
                      className="block font-serif text-4xl leading-tight py-2 border-b border-[#1A1A1A]/10"
                    >
                      {t(`nav.${it.key}`)}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#4A4A4A]">
                  <button onClick={() => setLang("es")} data-testid="mobile-lang-es" className={lang === "es" ? "text-[#1A1A1A]" : ""}>ES</button>
                  <span>·</span>
                  <button onClick={() => setLang("en")} data-testid="mobile-lang-en" className={lang === "en" ? "text-[#1A1A1A]" : ""}>EN</button>
                </div>
                <a href="/#contacto" onClick={() => setOpen(false)} className="kb-btn" data-testid="mobile-cta">
                  {t("nav.cta")}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
